import React from 'react';
import { useNavigate } from 'react-router-dom';
import './SmartStopLoss.css';

const SmartStopLoss: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="article-page">
      {/* Back Navigation */}
      <div className="article-nav">
        <button className="back-button" onClick={() => navigate('/learning-hub')}>
          ← Back to Learning Hub
        </button>
      </div>

      {/* Article Header */}
      <header className="article-header">
        <div className="article-badge">ESSENTIAL GUIDE</div>
        <h1 className="article-title">Smart Stop-Loss Techniques for Profitable Swing Trading</h1>
        <div className="article-meta">
          <span className="meta-item">📅 Updated December 2024</span>
          <span className="meta-item">⏱️ 15 min read</span>
          <span className="meta-item">📊 Essential for All Traders</span>
        </div>
      </header>

      {/* Article Content */}
      <article className="article-content">

        {/* Introduction */}
        <section className="content-section">
          <h2>Why Stop-Loss Is Your Best Friend in Swing Trading</h2>
          <p className="lead-text">
            In swing trading, protecting your capital is more important than chasing profits. A well-placed
            stop-loss is not just a safety net—it's a strategic tool that separates consistently profitable
            traders from those who blow up their accounts.
          </p>
          <p>
            Many traders lose money not because they can't identify good setups, but because they don't know
            where to place their stop-loss. Too tight, and you get stopped out on normal price fluctuations.
            Too wide, and a single losing trade can wipe out weeks of gains.
          </p>
          <div className="highlight-box">
            <strong>Key Principle:</strong> Your stop-loss should be placed at a level where, if hit,
            it invalidates your trade thesis—not just at an arbitrary percentage.
          </div>
        </section>

        {/* AI-Powered Stop-Loss */}
        <section className="content-section">
          <h2>1. AI-Powered Stop-Loss Technique (Our Recommended Method)</h2>
          <div className="ai-badge-container">
            <span className="ai-badge">🤖 AI-Suggested Strategy</span>
            <span className="market-badge">📊 Best for Small & Mid Cap Stocks</span>
          </div>
          <p className="lead-text">
            This is our proprietary AI-powered stop-loss technique developed by analyzing thousands of successful
            swing trades. The secret to consistent profitability isn't finding perfect entries—it's managing your
            exits properly. Let small losses happen quickly, and let big profits run as long as possible.
          </p>

          <h3>Step 1: Setting Your Initial Stop-Loss</h3>
          <p>
            When you make an entry, place your stop-loss approximately <strong>5% below your entry price</strong>,
            or at the bottom of the previous swing low. Your stop should typically be in the <strong>4-6% range</strong>—not
            tighter, not wider.
          </p>

          <div className="example-box">
            <h4>Example - Initial Entry:</h4>
            <p><strong>Stock:</strong> RELIANCE</p>
            <p><strong>Entry Price:</strong> ₹2,500</p>
            <p><strong>Previous Swing Low:</strong> ₹2,375</p>
            <p><strong>Initial Stop-Loss:</strong> ₹2,375 (5% below entry)</p>
            <p className="risk-calc">Initial Risk: ₹125 per share (5%)</p>
          </div>

          {/* Animated Illustration Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">🎬</div>
              <p className="placeholder-text">Animation: Initial Stop-Loss Placement</p>
              <p className="placeholder-desc">Shows price entry and 5% stop-loss below previous swing low</p>
            </div>
          </div>

          <h3>Step 2: Trailing Stop-Loss as Price Moves Up</h3>
          <p>
            Here's where most traders fail—they either exit too early or let profits evaporate. Follow these
            exact rules to trail your stop and lock in profits:
          </p>

          <div className="trailing-rules">
            <div className="rule-card">
              <div className="rule-number">Rule 1</div>
              <h4>If Price Moves Up 3%, Put Stop-Loss at 3% Below Entry</h4>
              <p>When your trade gains 3%, move your stop-loss to 3% below your entry price. This significantly reduces your risk from 5% to just 3%.</p>
              <div className="example-calc">
                <p><strong>Entry:</strong> ₹2,500 | <strong>Initial Stop:</strong> ₹2,375 (5% below entry)</p>
                <p><strong>Price moves to:</strong> ₹2,575 (+3%)</p>
                <p><strong>New Stop-Loss:</strong> ₹2,500 - 3% = ₹2,425 (3% below entry price)</p>
                <p className="result">✅ Risk reduced from 5% to 3%!</p>
              </div>
            </div>

            <div className="rule-card">
              <div className="rule-number">Rule 2</div>
              <h4>If Price Moves Up 5%, Put Stop-Loss at 1% Below Entry</h4>
              <p>When your trade gains 5%, move your stop-loss to just 1% below your entry price. This drastically reduces your risk while giving the trade room to breathe.</p>
              <div className="example-calc">
                <p><strong>Entry:</strong> ₹2,500 | <strong>Price moves to:</strong> ₹2,625 (+5%)</p>
                <p><strong>New Stop-Loss:</strong> ₹2,500 - 1% = ₹2,475 (1% below entry price)</p>
                <p className="result">✅ Risk reduced from 5% to just 1%!</p>
              </div>
            </div>

            <div className="rule-card">
              <div className="rule-number">Rule 3</div>
              <h4>If Price Moves Up 6%, Move Stop to Breakeven (0%)</h4>
              <p>When your trade gains 6%, move your stop-loss to your exact entry price. Now you have zero risk—even if there's a big move in the opposite direction, you cannot lose money.</p>
              <div className="example-calc">
                <p><strong>Entry:</strong> ₹2,500 | <strong>Price moves to:</strong> ₹2,650 (+6%)</p>
                <p><strong>New Stop-Loss:</strong> ₹2,500 (breakeven - 0% risk)</p>
                <p className="result">✅ Zero risk! No loss even if big reversal occurs!</p>
              </div>
            </div>

            <div className="rule-card">
              <div className="rule-number">Rule 4</div>
              <h4>Continue Trailing - No Fixed Target</h4>
              <p>
                As price keeps moving up, keep trailing your stop. <strong>Never set a fixed target.</strong> Let the
                market tell you when the move is over by hitting your trailing stop.
              </p>
              <div className="example-calc">
                <p><strong>Price moves to:</strong> ₹2,750 (+10%)</p>
                <p><strong>New Stop-Loss:</strong> ₹2,650 (4% profit locked)</p>
                <p><strong>Price moves to:</strong> ₹2,875 (+15%)</p>
                <p><strong>New Stop-Loss:</strong> ₹2,750 (10% profit locked)</p>
                <p className="result">🚀 Let winners run! Your profit keeps growing.</p>
              </div>
            </div>
          </div>

          {/* Animated Illustration Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">🎬</div>
              <p className="placeholder-text">Animation: Trailing Stop-Loss in Action</p>
              <p className="placeholder-desc">Shows price moving up with stop-loss trailing at 3%, 5%, 10%, 15%</p>
            </div>
          </div>

          <h3>Step 3: Two-Candle Low Trailing Method</h3>
          <p>
            For more precise trailing, use the <strong>last two candle lows</strong>. This adapts to the stock's
            actual price action instead of using fixed percentages.
          </p>

          <div className="method-card">
            <h4>How It Works:</h4>
            <ul>
              <li>Look at the last two daily candles</li>
              <li>Find the lowest point (low of the two candles)</li>
              <li>Place your stop-loss slightly below that low (₹5-10 below)</li>
              <li>As new candles form, keep updating to the new two-candle low</li>
            </ul>

            <div className="example-box">
              <h4>Example:</h4>
              <p><strong>Day 1:</strong> Low = ₹2,620</p>
              <p><strong>Day 2:</strong> Low = ₹2,640</p>
              <p><strong>Two-candle low:</strong> ₹2,620 (lower of the two)</p>
              <p><strong>Stop-Loss:</strong> ₹2,610 (₹10 below the low)</p>
              <p className="insight">This prevents getting stopped out by small intraday dips while protecting against real reversals.</p>
            </div>
          </div>

          <h3>Step 4: 9 EMA Trailing Stop (For Strong Uptrends)</h3>
          <p>
            When your stock is in a strong uptrend and running well, switch to the <strong>9 EMA trailing stop</strong>.
            This captures the entire trend without exiting too early.
          </p>

          <div className="method-card">
            <h4>9 EMA Trailing Stop Rules:</h4>
            <ol>
              <li><strong>Wait for candle to close below 9 EMA</strong> - Don't exit intraday, wait for daily close</li>
              <li><strong>Mark that day's low</strong> - Note the low of the candle that closed below 9 EMA</li>
              <li><strong>Place stop slightly below that low</strong> - Give ₹5-10 buffer below the marked low</li>
              <li><strong>Next day, if price breaks that marked low, EXIT immediately</strong> - Book your profit</li>
            </ol>

            <div className="example-box">
              <h4>Real Example:</h4>
              <p><strong>Stock running from ₹2,500 to ₹2,950</strong> - Strong uptrend</p>
              <p><strong>9 EMA:</strong> At ₹2,900</p>
              <p><strong>Day 15:</strong> Candle closes at ₹2,880 (below 9 EMA for first time)</p>
              <p><strong>Mark the low:</strong> That day's low was ₹2,865</p>
              <p><strong>Stop-Loss:</strong> ₹2,855 (₹10 below marked low)</p>
              <p><strong>Day 16:</strong> If price breaks ₹2,855, exit and book profit</p>
              <p className="result">✅ Profit Booked: ₹2,855 exit = +14.2% gain (₹355 per share)</p>
            </div>
          </div>

          {/* Animated Illustration Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">🎬</div>
              <p className="placeholder-text">Animation: 9 EMA Trailing Stop Method</p>
              <p className="placeholder-desc">Shows price riding above 9 EMA, closing below it, marking low, and exit signal</p>
            </div>
          </div>

          <div className="highlight-box">
            <strong>💡 Why This Strategy Works:</strong>
            <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
              <li><strong>Small Losses:</strong> Initial 4-6% stop keeps losses manageable</li>
              <li><strong>Quick Breakeven:</strong> At +3%, you can't lose money anymore</li>
              <li><strong>Big Winners:</strong> No fixed targets means 15-30% gains are possible</li>
              <li><strong>Trend Following:</strong> 9 EMA keeps you in strong trends longer</li>
            </ul>
            <p style={{ marginTop: '16px' }}>
              Result: <strong>Average winners of 12-20% vs average losers of 4-5% = Consistent profitability</strong>
            </p>
          </div>
        </section>

        {/* ATR-Based Stops */}
        <section className="content-section">
          <h2>2. ATR-Based Stop-Loss (Average True Range)</h2>
          <p>
            ATR-based stops adapt to a stock's volatility, giving you breathing room in volatile stocks
            while keeping stops tight on low-volatility plays.
          </p>

          <h3>How It Works:</h3>
          <ul>
            <li><strong>Calculate ATR (14-period)</strong> - This shows average price movement</li>
            <li><strong>Multiply by 2x to 3x</strong> - This is your stop distance</li>
            <li><strong>Place below entry</strong> - Entry Price - (ATR × Multiplier)</li>
          </ul>

          <div className="example-box">
            <h4>Example:</h4>
            <p><strong>Stock:</strong> RELIANCE</p>
            <p><strong>Entry Price:</strong> ₹2,500</p>
            <p><strong>ATR (14):</strong> ₹40</p>
            <p><strong>Stop-Loss:</strong> ₹2,500 - (₹40 × 2.5) = ₹2,400</p>
            <p className="risk-calc">Risk per share: ₹100 (4% risk)</p>
          </div>

          <div className="tip-box">
            <strong>💡 Pro Tip:</strong> Use 2x ATR for aggressive trades, 3x ATR for swing trades you want
            to hold longer. This prevents getting shaken out by normal volatility.
          </div>
        </section>

        {/* Support-Based Stops */}
        <section className="content-section">
          <h2>3. Support-Based Stop-Loss</h2>
          <p>
            This is the most intuitive method—place your stop just below a key support level. If price
            breaks support, your trade thesis is invalidated.
          </p>

          <h3>Where to Place Support-Based Stops:</h3>
          <ul>
            <li><strong>Below swing lows</strong> - Previous bottom that held during pullbacks</li>
            <li><strong>Below moving averages</strong> - 20 EMA, 50 SMA are common support zones</li>
            <li><strong>Below breakout levels</strong> - If buying a breakout, stop below breakout point</li>
            <li><strong>Below pattern lows</strong> - Base of cup & handle, flag, VCP</li>
          </ul>

          <div className="example-box">
            <h4>Example - Breakout Trade:</h4>
            <p><strong>Stock:</strong> TATAMOTORS breaks out at ₹750</p>
            <p><strong>Previous resistance (now support):</strong> ₹750</p>
            <p><strong>Stop-Loss:</strong> ₹735 (2% below breakout level)</p>
            <p className="insight">If price falls back below ₹750, the breakout failed—exit immediately.</p>
          </div>

          <div className="warning-box">
            <strong>⚠️ Warning:</strong> Don't place stops at obvious round numbers (₹500, ₹1,000, etc.).
            Market makers often hunt these levels. Place stops ₹5-10 below support instead.
          </div>
        </section>

        {/* Trailing Stops */}
        <section className="content-section">
          <h2>4. Advanced Trailing Stop-Loss Methods</h2>
          <p>
            Once your trade moves in your favor, a trailing stop helps you lock in profits while giving
            the trade room to run. This is how you maximize winners.
          </p>

          <h3>Trailing Stop Methods:</h3>

          <div className="method-card">
            <h4>Method A: Percentage Trailing Stop</h4>
            <p>Move stop-loss up as price rises, maintaining a fixed percentage gap.</p>
            <ul>
              <li>Entry: ₹1,000 | Initial Stop: ₹950 (5% risk)</li>
              <li>Price rises to ₹1,100 | New Stop: ₹1,045 (5% trailing)</li>
              <li>Price rises to ₹1,200 | New Stop: ₹1,140 (5% trailing)</li>
            </ul>
            <p className="result">Result: Locked in ₹140 profit per share even if stopped out.</p>
          </div>

          <div className="method-card">
            <h4>Method B: Moving Average Trailing Stop</h4>
            <p>Trail stop-loss below a rising moving average (20 EMA or 50 SMA).</p>
            <ul>
              <li>As long as stock stays above 20 EMA, hold the position</li>
              <li>If stock closes below 20 EMA, exit next day</li>
              <li>This captures the entire trend without exiting too early</li>
            </ul>
          </div>

          <div className="method-card">
            <h4>Method C: Chandelier Trailing Stop</h4>
            <p>Trail stop below the highest high minus 3x ATR.</p>
            <ul>
              <li>Highest High in trade: ₹1,250</li>
              <li>ATR: ₹30</li>
              <li>Trailing Stop: ₹1,250 - (₹30 × 3) = ₹1,160</li>
            </ul>
            <p className="insight">This adapts to volatility—wider stops in volatile moves, tighter in calm periods.</p>
          </div>
        </section>

        {/* Time-Based Stops */}
        <section className="content-section">
          <h2>5. Time-Based Stop-Loss</h2>
          <p>
            Sometimes the best stop is a time stop. If your trade isn't working within your expected
            timeframe, exit and redeploy capital elsewhere.
          </p>

          <div className="example-box">
            <h4>When to Use Time Stops:</h4>
            <ul>
              <li><strong>Breakout trades:</strong> If no follow-through in 2-3 days, exit</li>
              <li><strong>Earnings plays:</strong> Exit before earnings if trade hasn't moved</li>
              <li><strong>Dead money:</strong> If stock sideways for 2 weeks, exit and find better setups</li>
            </ul>
          </div>

          <div className="tip-box">
            <strong>💡 Opportunity Cost:</strong> Capital stuck in a dead trade can't be used for better opportunities.
            Don't fall in love with positions—cut losers quickly.
          </div>
        </section>

        {/* Position Sizing */}
        <section className="content-section">
          <h2>6. Position Sizing with Stop-Loss</h2>
          <p>
            Your stop-loss and position size must work together. Never risk more than 1-2% of your
            total capital on a single trade.
          </p>

          <div className="formula-box">
            <h4>Position Sizing Formula:</h4>
            <div className="formula">
              <strong>Shares to Buy = (Account Size × Risk %) ÷ (Entry Price - Stop-Loss Price)</strong>
            </div>

            <div className="example-box">
              <h4>Example Calculation:</h4>
              <p><strong>Account Size:</strong> ₹5,00,000</p>
              <p><strong>Risk per trade:</strong> 2% = ₹10,000</p>
              <p><strong>Entry Price:</strong> ₹1,000</p>
              <p><strong>Stop-Loss:</strong> ₹950</p>
              <p><strong>Risk per share:</strong> ₹50</p>
              <p className="result"><strong>Shares to Buy:</strong> ₹10,000 ÷ ₹50 = 200 shares</p>
            </div>
          </div>

          <div className="highlight-box">
            This way, you always risk exactly 2% (₹10,000) regardless of how wide or tight your stop is.
            This is how professional traders manage risk.
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="content-section">
          <h2>Common Stop-Loss Mistakes to Avoid</h2>

          <div className="mistake-card">
            <h4>❌ Mistake #1: Moving Stops to Avoid Losses</h4>
            <p>
              Never move your stop-loss farther away because "you need more room." If price hits your
              stop, your analysis was wrong—accept it and move on.
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #2: No Stop-Loss at All</h4>
            <p>
              "I'll just hold and wait" is not a strategy. Without stops, one bad trade can destroy
              your account. Always have a predefined exit.
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #3: Setting Mental Stops</h4>
            <p>
              Mental stops don't work. When price hits your "mental stop," emotions kick in and you
              convince yourself to hold. Always use actual stop-loss orders.
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #4: Stops Too Tight</h4>
            <p>
              Setting 1-2% stops on volatile stocks guarantees you'll get stopped out. Give your trades
              room to breathe based on ATR or volatility.
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #5: Not Trailing Stops</h4>
            <p>
              If your trade is up 15% but you're still using your initial stop, you're risking all
              those gains. Trail your stops to lock in profits as the trade moves.
            </p>
          </div>
        </section>

        {/* Quick Reference */}
        <section className="content-section">
          <h2>Quick Reference Guide</h2>

          <div className="reference-table">
            <table>
              <thead>
                <tr>
                  <th>Trade Type</th>
                  <th>Recommended Stop-Loss Method</th>
                  <th>Typical Risk</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Breakout Trade</td>
                  <td>Below breakout level (2-3% below)</td>
                  <td>2-3%</td>
                </tr>
                <tr>
                  <td>Pullback Entry</td>
                  <td>Below recent swing low or support</td>
                  <td>3-5%</td>
                </tr>
                <tr>
                  <td>Volatile Stock (High ATR)</td>
                  <td>2.5-3x ATR below entry</td>
                  <td>5-8%</td>
                </tr>
                <tr>
                  <td>Low Volatility Stock</td>
                  <td>2x ATR or tight support</td>
                  <td>2-4%</td>
                </tr>
                <tr>
                  <td>Swing Trade (Multi-week)</td>
                  <td>Below 50 SMA or 3x ATR</td>
                  <td>5-8%</td>
                </tr>
                <tr>
                  <td>Momentum Trade</td>
                  <td>Trail with 20 EMA</td>
                  <td>Variable</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Summary */}
        <section className="content-section summary-section">
          <h2>Key Takeaways</h2>
          <ul className="takeaway-list">
            <li>✅ Always use stop-losses—no exceptions. It's your primary risk management tool.</li>
            <li>✅ Match stop-loss method to your trade type and stock volatility (ATR-based for volatile stocks).</li>
            <li>✅ Never risk more than 1-2% of your account on any single trade.</li>
            <li>✅ Trail your stops to lock in profits as trades move in your favor.</li>
            <li>✅ Place stops based on technical levels, not arbitrary percentages.</li>
            <li>✅ Use time stops to cut dead trades and free up capital.</li>
            <li>✅ Never move stops farther away to avoid losses—accept when you're wrong.</li>
          </ul>

          <div className="final-note">
            <strong>Remember:</strong> The goal of swing trading isn't to never lose—it's to keep losses
            small and let winners run. Smart stop-loss placement is what makes this possible.
          </div>
        </section>

      </article>

      {/* Related Articles */}
      <section className="related-articles">
        <h3>Continue Learning</h3>
        <div className="related-grid">
          <div className="related-card" onClick={() => navigate('/learning-hub/momentum-ai-guide')}>
            <div className="related-icon">🤖</div>
            <h4>How to Trade Momentum AI Screener</h4>
            <p>Learn entry, exit, and risk management for momentum trades</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub/vcp-guide')}>
            <div className="related-icon">🎯</div>
            <h4>How to Trade VCP Screener</h4>
            <p>VCP pattern identification and trading strategies</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub')}>
            <div className="related-icon">📚</div>
            <h4>View All Guides</h4>
            <p>Explore all swing trading tutorials</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="article-cta">
        <h3>Ready to Apply These Techniques?</h3>
        <p>Use our AI screeners to find high-probability swing trading setups</p>
        <button className="cta-button" onClick={() => navigate('/screener/momentum-ai')}>
          Try AI Screeners Now →
        </button>
      </section>

    </div>
  );
};

export default SmartStopLoss;
